import { AlertCircle, AlertTriangle, Info, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

type ErrorSeverity = "error" | "warning" | "info";

interface ErrorStateProps {
  title: string;
  message: string;
  severity?: ErrorSeverity;
  onRetry?: () => void;
  retryLabel?: string;
  onDismiss?: () => void;
  className?: string;
}

const severityConfig = {
  error: {
    icon: XCircle,
    variant: "destructive" as const,
    iconColor: "text-destructive",
  },
  warning: {
    icon: AlertTriangle,
    variant: "default" as const,
    iconColor: "text-yellow-500",
  },
  info: {
    icon: Info,
    variant: "default" as const,
    iconColor: "text-blue-500",
  },
};

export const ErrorState = ({
  title,
  message,
  severity = "error",
  onRetry,
  retryLabel = "Try Again",
  onDismiss,
  className,
}: ErrorStateProps) => {
  const config = severityConfig[severity];
  const Icon = config.icon;

  return (
    <div className={cn("animate-shake", className)}>
      <Alert variant={config.variant}>
        <Icon className={cn("h-4 w-4", config.iconColor)} />
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="mt-2">
          {message}
          
          {/* Actions */}
          {(onRetry || onDismiss) && (
            <div className="flex gap-2 mt-4">
              {onRetry && (
                <Button
                  onClick={onRetry}
                  size="sm"
                  variant={severity === "error" ? "default" : "outline"}
                >
                  {retryLabel}
                </Button>
              )}
              {onDismiss && (
                <Button
                  onClick={onDismiss}
                  size="sm"
                  variant="ghost"
                >
                  Dismiss
                </Button>
              )}
            </div>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
};

// Inline error variant for forms
interface InlineErrorProps {
  message: string;
  className?: string;
}

export const InlineError = ({ message, className }: InlineErrorProps) => {
  return (
    <div className={cn(
      "flex items-center gap-2 text-sm text-destructive animate-shake",
      className
    )}>
      <AlertCircle className="w-4 h-4" />
      <span>{message}</span>
    </div>
  );
};
